/* ============================================================================
   ACEPLORE · module-facets.js — AJAX filtering, sorting, pagination, view toggle.
   Custom element: <lumina-facets>. Uses the Section Rendering API to swap the
   product grid + facet UI without a full reload, and keeps the URL shareable.
   ========================================================================== */
(function () {
  'use strict';
  const { utils, events } = window.Lumina;

  class LuminaFacets extends window.Lumina.Element {
    connectedCallback() {
      this.section = this.dataset.section;
      this.container = this.querySelector('[data-results-container]');
      this.facetsEl = this.querySelector('[data-facets]');
      this.debouncedFetch = utils.debounce((url, push, focusId) => this.fetchResults(url, push, focusId), 320);
      this.bind();
      this.bindMobile();
      this.bindView();
      this.bindInfinite();
      this.listen(window, 'popstate', () => this.fetchResults(location.href, false));
    }

    // Infinite scroll: when the merchant picks the "infinite" pagination type,
    // the load-more wrapper carries [data-infinite] — auto-load the next page
    // as it approaches the viewport. The wrapper element survives loadMore()
    // (only its innerHTML is swapped), so one observer covers every page.
    bindInfinite() {
      if (this.infiniteIO) { this.infiniteIO.disconnect(); this.infiniteIO = null; }
      const wrap = this.container && this.container.querySelector('[data-infinite]');
      if (!wrap || !('IntersectionObserver' in window)) return;
      this.infiniteIO = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const btn = wrap.querySelector('[data-load-more]');
          if (btn && !btn.disabled) this.loadMore(btn);
        });
      }, { rootMargin: '600px 0px' });
      this.infiniteIO.observe(wrap);
    }

    bind() {
      // Filter inputs (checkboxes / price) — live update
      this.listen(this, 'input', (e) => {
        if (e.target.matches('[data-facet-input]')) this.onFormChange();
      });
      this.listen(this, 'change', (e) => {
        if (e.target.matches('[data-sort]')) this.onSort(e.target.value);
      });
      // Active-pill removal + clear-all (anchor based)
      this.listen(this, 'click', (e) => {
        const remove = e.target.closest('[data-facet-remove], [data-facets-clear]');
        if (remove && remove.tagName === 'A') { e.preventDefault(); this.fetchResults(remove.href, true, '', true); }
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more, true); }
      });
    }

    currentForm() { return this.querySelector('[data-facets-form]'); }

    onFormChange() {
      const form = this.currentForm();
      if (!form) return;
      const params = new URLSearchParams(new FormData(form));
      // strip empty price values
      for (const [k, v] of [...params.entries()]) if (v === '') params.delete(k);
      const sort = this.querySelector('[data-sort]');
      if (sort && sort.value) params.set('sort_by', sort.value);
      const url = `${this.dataset.resultsUrl}?${params.toString()}`;
      const active = document.activeElement;
      const focusId = active && this.facetsEl?.contains(active) ? active.id : '';
      this.debouncedFetch(url, true, focusId);
    }

    onSort(value) {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      this.fetchResults(url.toString(), true);
    }

    async fetchResults(url, push, focusId, restoreFacetFocus = false) {
      this.setLoading(true);
      // Latest-wins: abort the previous in-flight request so an out-of-order
      // response can't leave the grid and the URL mismatched.
      if (this._ctrl) this._ctrl.abort();
      const ctrl = (this._ctrl = new AbortController());
      try {
        const u = new URL(url, location.origin);
        u.searchParams.set('section_id', this.section);
        const res = await fetch(u.toString(), { signal: ctrl.signal });
        if (!res.ok) throw new Error(`Results request failed (${res.status})`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newResults = doc.querySelector('[data-results-container]');
        const newFacets = doc.querySelector('[data-facets] .lm-facets__panel');
        const newCount = doc.querySelector('[data-results-count]');
        if (newResults) this.container.innerHTML = newResults.innerHTML;
        if (newFacets && this.facetsEl) {
          const cur = this.facetsEl.querySelector('.lm-facets__panel');
          if (cur) cur.innerHTML = newFacets.innerHTML;
        }
        // The search template's count lives in the page header OUTSIDE this
        // element — fall back to document scope so it doesn't go stale.
        if (newCount) { const c = this.querySelector('[data-results-count]') || document.querySelector('[data-results-count]'); if (c) c.textContent = newCount.textContent; }
        if (push) {
          const clean = new URL(url, location.origin);
          clean.searchParams.delete('section_id');
          history.pushState({}, '', clean.toString());
        }
        events.emit('section:load', { target: this.container });
        // Swapped-in cards carry .lm-reveal (held at opacity 0) — re-arm the
        // observer or the whole grid renders invisible.
        if (window.Lumina.initReveal) window.Lumina.initReveal(this.container);
        else this.container.querySelectorAll('.lm-reveal').forEach((el) => el.classList.add('is-visible'));
        this.applyView(utils.storage.get('lumina:view', 'grid'));
        this.bindInfinite();
        if (focusId) requestAnimationFrame(() => document.getElementById(focusId)?.focus({ preventScroll: true }));
        else if (restoreFacetFocus) requestAnimationFrame(() => this.facetsEl?.querySelector('[data-facet-remove], [data-facets-clear], [data-facet-input], [data-facets-close]')?.focus({ preventScroll: true }));
        this.scrollToTop();
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        Lumina.toast(utils.t('resultsError', "Couldn't update results. Please try again."), { type: 'error' });
      } finally {
        // Only the latest request may clear the busy state — an aborted
        // predecessor resolving late must not blank it mid-load.
        if (this._ctrl === ctrl) this.setLoading(false);
      }
    }

    async loadMore(btn, focusAfter = false) {
      const url = btn.dataset.url;
      if (!url) return;
      btn.classList.add('is-loading');
      btn.disabled = true;
      try {
        const u = new URL(url, location.origin);
        u.searchParams.set('section_id', this.section);
        const res = await fetch(u.toString());
        if (!res.ok) throw new Error(`Pagination request failed (${res.status})`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const grid = this.container.querySelector('[data-product-grid]');
        const newCards = doc.querySelectorAll('[data-product-grid] [data-product-card]');
        const firstNew = newCards[0];
        newCards.forEach((c) => grid.appendChild(c));
        if (window.Lumina.initReveal) window.Lumina.initReveal(grid);
        else grid.querySelectorAll('.lm-reveal').forEach((el) => el.classList.add('is-visible'));
        const newPag = doc.querySelector('[data-pagination]');
        const curPag = this.container.querySelector('[data-pagination]');
        if (newPag && curPag) curPag.innerHTML = newPag.innerHTML;
        // replaceState, not pushState: auto-loaded pages must not stack history
        // entries, or Back walks through every infinite-scroll page.
        history.replaceState({}, '', url);
        events.emit('section:load', { target: grid });
        let status = this.querySelector('[data-load-more-status]');
        if (!status) {
          status = document.createElement('span');
          status.className = 'visually-hidden';
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
          status.dataset.loadMoreStatus = '';
          this.appendChild(status);
        }
        status.textContent = `${newCards.length} more products loaded.`;
        if (focusAfter) requestAnimationFrame(() => (firstNew?.querySelector('a[href], button') || this.querySelector('[data-load-more]'))?.focus({ preventScroll: true }));
      } catch (_) {
        Lumina.toast(utils.t('resultsError', "Couldn't update results. Please try again."), { type: 'error' });
      } finally { btn.classList.remove('is-loading'); btn.disabled = false; }
    }

    disconnectedCallback() {
      if (this.infiniteIO) { this.infiniteIO.disconnect(); this.infiniteIO = null; }
      if (this._facetsLocked) { utils.lockScroll(false); this._facetsLocked = false; }
      if (super.disconnectedCallback) super.disconnectedCallback();
    }

    setLoading(on) { this.container.classList.toggle('is-loading', on); this.setAttribute('aria-busy', String(on)); }
    scrollToTop() {
      const top = this.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--lm-header-height')) || 72) - 16;
      window.scrollTo({ top, behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    bindMobile() {
      const open = this.querySelector('[data-facets-open]');
      if (!open || !this.facetsEl) return;
      const media = window.matchMedia('(max-width:989px)');
      const overlay = this.querySelector('[data-facets-overlay]');
      const close = () => {
        this.facetsEl.classList.remove('is-open');
        overlay?.classList.remove('is-open');
        open.setAttribute('aria-expanded', 'false');
        if (this._facetsRelease) { this._facetsRelease(); this._facetsRelease = null; }
        if (this._facetsLocked) { utils.lockScroll(false); this._facetsLocked = false; }
        this.facetsEl.removeAttribute('role');
        this.facetsEl.removeAttribute('aria-modal');
        if (media.matches) {
          this.facetsEl.setAttribute('aria-hidden', 'true');
          this.facetsEl.setAttribute('inert', '');
        } else {
          this.facetsEl.removeAttribute('aria-hidden');
          this.facetsEl.removeAttribute('inert');
        }
      };
      this.listen(open, 'click', () => {
        if (!media.matches) return;
        if (this.facetsEl.classList.contains('is-open')) return;
        this.facetsEl.classList.add('is-open');
        overlay?.classList.add('is-open');
        this.facetsEl.removeAttribute('inert');
        this.facetsEl.setAttribute('role', 'dialog');
        this.facetsEl.setAttribute('aria-modal', 'true');
        this.facetsEl.setAttribute('aria-hidden', 'false');
        utils.lockScroll(true);
        this._facetsLocked = true;
        this._facetsRelease = utils.trapFocus(this.facetsEl, open);
        open.setAttribute('aria-expanded', 'true');
      });
      this.listen(this, 'click', (e) => { if (e.target.closest('[data-facets-close]')) close(); });
      this.listen(document, 'keydown', (e) => { if (e.key === 'Escape' && this.facetsEl.classList.contains('is-open')) close(); });
      this.listen(media, 'change', close);
      close();
    }

    bindView() {
      const toggle = this.querySelector('[data-view-toggle]');
      if (!toggle) return;
      const saved = utils.storage.get('lumina:view', 'grid');
      this.applyView(saved);
      this.listen(toggle, 'click', (e) => {
        const btn = e.target.closest('[data-view]');
        if (!btn) return;
        this.applyView(btn.dataset.view);
        utils.storage.set('lumina:view', btn.dataset.view);
      });
    }
    applyView(view) {
      const grid = () => this.container.querySelector('[data-product-grid]');
      this.querySelectorAll('[data-view]').forEach((b) => {
        const active = b.dataset.view === view;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', String(active));
      });
      const g = grid();
      if (g) g.classList.toggle('lm-product-grid--list', view === 'list');
    }
  }
  if (!customElements.get('lumina-facets')) customElements.define('lumina-facets', LuminaFacets);
})();
